import type {
	FieldReturnTypes,
	Fields,
	KindSchema,
	SchemaFieldTypes,
	SchemaTypeWithComponentProp,
} from "./types";
import type { Core, Schema } from "../../..";
import type * as PageContentType_Fields from "../../../types/schema-fields/page-content-type-fields";
import type { PageContentTypeFields } from "../../../types/schema-fields/page-content-type-fields";
import type {
	ThemeVisualUniqueSelection,
	ThumbnailOption,
} from "../../../types/schema-fields/props";
import type { SimpleContentTypeFields } from "../../../types/schema-fields/simple-content-type-fields";
import type * as UI_Fields from "../../../types/schema-fields/ui-fields";
import type { UIFields } from "../../../types/schema-fields/ui-fields";
import type { UIFormFields } from "../../../types/schema-fields/ui-form-fields";
import type { UIFormTemplateFields } from "../../../types/schema-fields/ui-form-template-fields";
import type { UITemplateFields } from "../../../types/schema-fields/ui-template-fields";
import type { Module } from "../../../types/schemas";

import { spawn } from "node:child_process";
import path from "node:path";
import { fileURLToPath } from "node:url";

import { packageDirectorySync } from "pkg-dir";
import prettier from "prettier";

import { contentTypeSuffix as cts, interfaceSuffix as isf } from "./constants";
import { ERRORS } from "./errors";

const __dirname = path.dirname(fileURLToPath(import.meta.url));

/**
 * Return a string separated by the pipe char "|" using the `value` from object array.
 */
function getGenericFromOptions<T = unknown>(options: Array<{ value: T }>) {
	return options
		.map(({ value }) => (typeof value === "string" ? `"${value}"` : value))
		.join("|");
}

/**
 * Return a string separated by the pipe char "|" using the `name` from object array.
 */
function getGenericFromCheckBoxOptions<T>(options: Array<{ name: T }>) {
	return options
		.map(({ name }) => (typeof name === "string" ? `"${name}"` : name))
		.join("|");
}

// for components containers
// whitelist.length = 1
// whitelist.length > 1
async function getGenericFromWhitelist(
	whiteList: Array<string>,
	interfaceSuffix: string,
) {
	if (whiteList.length > 1) {
		return `Record<string, 
			${whiteList.map((whiteListItem) => `${whiteListItem}${interfaceSuffix}`).join("|")}
			>`;
	}

	// Si solo hay un elemento
	return whiteList.map((item) => `${item}${isf}`).join("");
}

function returnType(type: SchemaFieldTypes) {
	const typeObj: Record<SchemaFieldTypes, FieldReturnTypes> = {
		AIReferenceField: "Fields.AIReference",
		FormCategorySelect: "Fields.FormCategorySelect",
		FormContainer: "Fields.FormContainer",
		FormFieldArray: "Fields.FormFieldArray",
		ArrayFieldGroup: "Fields.ArrayFieldGroup",
		AsyncCheckGroup: "Fields.AsyncCheckGroup",
		AsyncSelect: "Fields.AsyncSelect",
		CheckGroup: "Fields.CheckGroup",
		ColorPicker: "Fields.ColorPicker",
		ComponentArray: "Fields.ComponentArray",
		ComponentContainer: "Fields.ComponentContainer",
		ConditionalField: "Fields.Conditional",
		DateField: "Fields.Date",
		FieldGroup: "Fields.FieldGroup",
		FieldsDivider: "Fields.FieldsDivider",
		FileField: "Fields.File",
		HeadingField: "Fields.Heading",
		ImageField: "Fields.Image",
		LinkField: "Fields.Link",
		MultiCheckSelect: "Fields.MultiCheckSelect",
		MultiCheckSelectGroup: "Fields.MultiCheckSelectGroup",
		NoteField: "Fields.Note",
		NumberField: "Fields.Number",
		RadioGroup: "Fields.RadioGroup",
		ReferenceField: "Fields.Reference",
		RichText: "Fields.RichText",
		Select: "Fields.Select",
		SliderField: "Fields.Slider",
		TagsField: "Fields.Tags",
		TextArea: "Fields.TextArea",
		TextField: "Fields.Text",
		TimeField: "Fields.Time",
		ToggleField: "Fields.Toggle",
		UniqueCheck: "Fields.UniqueCheck",
		UrlField: "Fields.Url",
		VisualUniqueSelection: "Fields.VisualUniqueSelection",
		Wysiwyg: "Fields.Wysiwyg",
	};
	return typeObj[type];
}

/** Build a interface prop based on the field from the schema.
 * Optionally it can receives a custom value for the type and a generic string to use as generic value
 *
 * @example
 * getGriddoFieldPropType(TextFieldObjectFromSchema)
 * // -> TextFieldObjectFromSchema.key: Fields.TextField
 * getGriddoFieldPropType(TextFieldObjectFromSchema, 'MyCustomValue', 'foo')
 * // -> TextFieldObjectFromSchema.key: MyCustomValue<foo>
 */
type GetInterfacePropTypeOptions = {
	returnTypeName?: FieldReturnTypes;
	generic?: string;
};

/**
 * Return a serialized type from a Griddo schema field.
 *
 * @param Fields A Griddo field object
 * @param options An options object
 * @returns A string that represent a interface prop type.
 * @example
 * interface SomeInterface {
 * 	prop: boolean; // this function returns a string for this: `prop: boolean`
 * }
 */
function getGriddoFieldPropType(
	field: Fields,
	options?: GetInterfacePropTypeOptions,
) {
	const { returnTypeName, generic } = options || {};

	// The field has `hideable: true`
	const hasHideable = "hideable" in field ? Boolean(field.hideable) : false;
	// Generic part
	const genericStr = generic ? `<${generic}>` : "";
	// The field type is from field.type prop or custom
	const returnTypeNameStr = returnTypeName || returnType(field.type);
	// Field type with generic: field<generic>
	const returnTypeNameWithGeneriStr = `${returnTypeNameStr}${genericStr}`;
	// Field type wrapped in Hideable helper type: Hideable<field<generic>>
	const typeWrappedWithHideable = hasHideable
		? `Hideable<${returnTypeNameWithGeneriStr}>`
		: `${returnTypeNameWithGeneriStr}`;

	// JSDoc
	const jsDocString = field._autoType?.doc || "";
	// prop?: Type
	const typeString = `${field.key}?: ${typeWrappedWithHideable}`;

	return `
		${jsDocString}
		${typeString}
	`;
}

async function prettify(input: string) {
	return (
		await prettier.format(input, {
			useTabs: true,
			printWidth: 120,
			parser: "typescript",
		})
	).replace(/^\s*\n/gm, "");
}

const IS_COMPONENT_LIBRARY = __dirname.includes("node_modules");

/**
 * Returns the instance or monorepo paths.
 *
 * @param customPath The path for the instance components
 */
function resolveInstancePath() {
	const cwd = __dirname;
	const componentsPath = IS_COMPONENT_LIBRARY
		? "../../../"
		: "../griddo-components";

	return path.resolve(packageDirectorySync({ cwd }), componentsPath);
}

function extractMergedSchemas(structuredData: any) {
	// Preprocesing some schemas
	// These variables will be get directly from the griddo.config.ts in the
	// future. Now we need to extract them from
	// `configFile.schemas.structuredData` and `configFile.schemas.all`
	const categoryContentTypes = extractCategoryContentTypes(structuredData);
	const pageContentTypes = extractPageContentTypes(structuredData);
	const simpleContentTypes = extractSimpleContentTypes(structuredData);

	return {
		categoryContentTypes,
		pageContentTypes,
		simpleContentTypes,
	};
}

function extractCategoryContentTypes(
	categoryContentTypeSchemas: Record<string, Schema.CategoryContentType>,
) {
	const objs: Record<string, Schema.CategoryContentType> = {};
	for (const key in categoryContentTypeSchemas) {
		const schema = categoryContentTypeSchemas[key];
		if (!schema.fromPage && schema.taxonomy) {
			objs[key] = schema;
		}
	}

	return objs;
}

function extractPageContentTypes(
	pageContentTypeSchemas: Record<string, Schema.PageContentType>,
) {
	const objs: Record<string, Schema.PageContentType> = {};
	for (const key in pageContentTypeSchemas) {
		const schema = pageContentTypeSchemas[key];
		if (schema.fromPage) {
			objs[key] = schema;
		}
	}

	return objs;
}

function extractSimpleContentTypes(
	simpleContentTypeSchemas: Record<string, Schema.SimpleContentType>,
) {
	const objs: Record<string, Schema.SimpleContentType> = {};
	for (const key in simpleContentTypeSchemas) {
		const schema = simpleContentTypeSchemas[key];
		if (!schema.fromPage && !schema.taxonomy) {
			objs[key] = schema;
		}
	}

	return objs;
}

export function loadConfigTs(configPath: string) {
	return new Promise((resolve, reject) => {
		const abs = path.resolve(configPath);

		const child = spawn(
			process.execPath,
			[
				"--import",
				"tsx",
				"-e",
				`import('${abs}').then(m => console.log(JSON.stringify(m.default ?? m)))`,
			],
			{
				stdio: ["ignore", "pipe", "inherit"],
			},
		);

		let output = "";

		child.stdout.on("data", (chunk) => {
			output += chunk;
		});

		child.on("error", reject);

		child.on("close", (code) => {
			if (code !== 0) {
				return reject(new Error(`Config process exited with code ${code}`));
			}
			try {
				resolve(JSON.parse(output));
			} catch (err) {
				reject(err);
			}
		});
	});
}

function readConfig() {
	// const configPath = path.join(resolveInstancePath(), "griddo.config.ts");
	// const config = require(configPath);
	// const configWithFallbacks = {
	// 	interfaceSuffix: config.autotypes?.interfaceSuffix || "Props",
	// 	contentTypeSuffix: config.autotypes?.contentTypeSuffix || "ContentType",
	// 	publicApiSuffix: config.autotypes?.publicApiSuffix || "List",
	// };
	// return configWithFallbacks;
}

/**
 * Return an optional property (the key in some Griddo field) and its value
 * (some Griddo field type) serialized based on a schema field.
 *
 * @param field A Griddo field object
 * @param kind Schema type in order to later do some operations with fields
 * @param options Object options
 * @example
 * getTypeFromField({ type: 'TextField', key: 'cardTitle', ... }, "component")
 * // returns -> "cardTitle?: Fields.Text"
 */
function getTypeFromField(
	field:
		| UIFields
		| UIFormFields
		| UITemplateFields
		| UIFormTemplateFields
		| SimpleContentTypeFields
		| PageContentTypeFields,
	kind: KindSchema,
	options: {
		hasDistributorData: boolean;
		getStaticData: boolean;
		moduleSchemas: Array<Module | undefined>;
		schema: SchemaTypeWithComponentProp;
		allSchemas: any;
		autotypesConfig: Core.Config["autotypes"];
		_from?: string;
		_fromKey?: string;
	},
): string | undefined {
	// Config
	const interfaceSuffix = options.autotypesConfig?.interfaceSuffix || isf;
	const contentTypeSuffix = options.autotypesConfig?.contentTypeSuffix || cts;
	//
	// Ignore - Bypass
	//
	// Empezamos ignorando ciertos fields que no necesitamos que generen types
	// aunque estén en los schemas. De esta manera los quitamos del proceso en
	// primera instancia y evitamos errores en los types de esta función.
	if (field.type === "FieldsDivider") {
		return "// FieldsDivider";
	}

	// Ignore type generation from schema _autoType prop
	if (field._autoType?.ignore) {
		return "// Ignored by AutoType.ignore";
	}

	//
	// Go!
	//
	const { type, _autoType, key } = field;
	const { hasDistributorData, getStaticData } = options;

	// If the field is missing the prop key or type, return an "error" string.
	if (!type || !key) {
		return `
			/** · (ノ-_-)ノ ミ ┴┴ You forgot to write the \`type\` | \`key\` or some mandatory prop */
		`;
	}

	// Simple fields
	// { type: "TextField", key: "foo" } --> "foo?: Fields.Text"
	if (
		type === "DateField" ||
		type === "FileField" ||
		type === "ImageField" ||
		type === "NoteField" ||
		type === "NumberField" ||
		type === "RichText" ||
		type === "SliderField" ||
		type === "TagsField" ||
		type === "TextArea" ||
		type === "TextField" ||
		type === "TimeField" ||
		type === "ToggleField" ||
		type === "UniqueCheck" ||
		type === "UrlField" ||
		type === "Wysiwyg"
	) {
		return getGriddoFieldPropType(field);
	}

	// MultiCheckSelect
	// { type: "MultiCheckSelect", key: "foo" } -->  "foo?: Fields.MultiCheckSelectGroup<Relations, true>"
	if (type === "MultiCheckSelect") {
		// - MultiCheckSelect in a ContentType schema (page or simple)
		//
		// Cuando está dentro de un ContentType el field devuelve cosas
		// distintas a cuando está dentro de un schema de UI y tiene en cuenta:
		// - Si está siendo requerido por API pública (useList)
		// - Si tiene relations
		// - Se añade el contentType relacionado para poder hacera autocomplete
		if (kind === "pageContentType" || kind === "simpleContentType") {
			const relationData = `${field.source}${contentTypeSuffix}${interfaceSuffix}`;
			return getGriddoFieldPropType(field, {
				generic: `Relations, FromList, ${relationData}<false, false>, FromDistributor`,
			});
		}

		// - Rest of schemas
		//
		// { type: "MultiCheckSelect", key: "foo" } -->  "foo?: Fields.MultiCheckSelectGroup<Relations>"
		return getGriddoFieldPropType(field, {
			generic: "true",
		});
	}

	// AsyncCheckGroup
	// Duplicate from MultiCheckSelect
	if (type === "AsyncCheckGroup") {
		if (kind === "pageContentType" || kind === "simpleContentType") {
			const relationData = `${field.source}${contentTypeSuffix}${interfaceSuffix}`;
			return getGriddoFieldPropType(field, {
				generic: `Relations, FromList, ${relationData}<false, false>, FromDistributor`,
			});
		}

		return getGriddoFieldPropType(field, {
			generic: "true",
		});
	}

	// MultiCheckSelectGroup
	// { type: "MultiCheckSelectGroup", key: "foo" } -->  "foo?: Fields.MultiCheckSelectGroup<>"
	if (type === "MultiCheckSelectGroup") {
		const keys = field.elements.map((element) => `"${element.key}"`).join("|");
		const relations = field._autoType?.relations;
		const generic = [keys, relations].join(",");

		return getGriddoFieldPropType(field, { generic });
	}

	// ColorPicker
	// { type: "ColorPickerFixed", key: "foo" } -->  "foo?: Fields.ColorPickerFixed<>"
	if (type === "ColorPicker") {
		if (field.isThemePalette) {
			return getGriddoFieldPropType(field, {
				returnTypeName: "Fields.ColorPickerFixed",
			});
		}

		return getGriddoFieldPropType(field);
	}

	// HeadingField, RadioGroup, Select
	// Generic from options.values
	// { type: "HeadingField", key: "foo" } --> "foo?: Fields.Heading<>"
	if (type === "HeadingField" || type === "RadioGroup" || type === "Select") {
		const { options } = field;
		const generic = getGenericFromOptions(options);

		return getGriddoFieldPropType(field, { generic });
	}

	// CheckGroup
	// Generic from options.name
	// { type: "CheckGroup", key: "foo" } --> "Fields.CheckGroup<>
	if (type === "CheckGroup") {
		const { options } = field;
		const generic = getGenericFromCheckBoxOptions(options);

		return getGriddoFieldPropType(field, { generic });
	}

	// LinkField
	// Generic from whitelist
	// type === "LinkField" --> key: Fields.Link<{video:Video, image:Image}>
	if (type === "LinkField") {
		const { whiteList } = field;

		let generic = "";
		// getGenericFromWhiteListAndModalDefaultValues
		if (whiteList.length > 1 && "default" in options.schema) {
			const defVal = options.schema.default[field.key].modal;
			const genericStr = Object.keys(defVal)
				.map((key) => `${key}?: ${defVal[key].component}${interfaceSuffix}`)
				.join(",");
			generic = `{${genericStr}}`;
		} else {
			generic = `${whiteList[0]}${interfaceSuffix}`;
		}

		return getGriddoFieldPropType(field, { generic });
	}

	// AsyncSelect
	// { type: "AsyncSelect", key: "foo" } --> "foo?: Fields.{AsyncSelectType}"
	// TODO: waiting for the field documentation
	if (type === "AsyncSelect") {
		// Entities
		if ("entity" in field) {
			// Menu
			if (field.entity === "menu_containers") {
				return `\n${key}?: Menu`;
			}
			// // Pages
			// if (field.entity === "sites") {
			// 	return `${key}?: Fields.Menu;`;
			// }
			// // Sites
			// if (field.entity === "pages") {
			// 	return `${key}?: Fields.Menu;`;
			// }
		}

		if (kind === "pageContentType" || kind === "simpleContentType") {
			const relationData = `${field.source}${contentTypeSuffix}${interfaceSuffix}`;
			return getGriddoFieldPropType(field, {
				generic: `Relations, FromList, ${relationData}<false, false>, FromDistributor`,
			});
		}

		return `\n${key}?: Fields.AsyncSelect;`;
	}

	// FieldGroup
	// { type: "FieldGroup", key: "foo" } --> "foo?: Fields.???"
	// <Recursive>
	if (type === "FieldGroup") {
		return `
			// << ${type}
			${field.fields.map((field) => getTypeFromField(field, kind, options)).join("")}
			// end >>
		`;
	}

	// ArrayFieldGroup
	if (type === "ArrayFieldGroup") {
		const doc = _autoType?.doc || "";

		return `
			\n${doc}\n${key}?: Fields.ArrayFieldGroup<{${field.fields
				.map((childField) =>
					getTypeFromField(childField, kind, {
						...options,
						_from: "ArrayFieldGroup",
						_fromKey: key,
					}),
				)
				.join("")}}>
		`;
	}

	// ConditionalField
	// <Recursive>
	if (type === "ConditionalField") {
		const conditionalChildrenFields = field.fields.map((field) => {
			return getTypeFromField(field, kind, options);
		});
		const doc = _autoType?.doc || "";
		const conditionalHasHideable = "hideable" in field ? field.hideable : false;
		const startHideable = conditionalHasHideable ? "Hideable<" : "";
		const endHideable = conditionalHasHideable ? ">" : "";

		return `
			// << ${type}
			\n${doc}\n${key}?: ${startHideable}${returnType(type)}<${field.options
				.map((option) => {
					return typeof option.value === "string"
						? `"${option.value}"`
						: option.value;
				})
				.join("|")}>${endHideable};
			${conditionalChildrenFields.join("")}
			// end >>
		`;
	}

	if (type === "FormFieldArray") {
		// El type de Griddo es dinstinto para un ComponentArray si este está
		// dentro de un template.
		const returnTypeName =
			kind === "template" || kind === "pageContentType"
				? "TemplateSection"
				: kind === "formTemplate"
					? "FormFieldArray"
					: "ComponentArray";

		const optional = kind === "template" ? "" : "?";

		const { key, _autoType, whiteList } = field;
		const doc = _autoType?.doc || "";

		// TODO: Research to compute this with type itself.
		// SHAME: Remove anys!!
		const getModulesFromTemplateSectionList = (
			modules: Array<Module | undefined>,
			templateName: string,
			templateSection: string,
		): Array<Module | undefined> => {
			return modules
				.filter((entry: any) => {
					if (
						entry &&
						"schemaType" in entry &&
						entry?.schemaType === "module"
					) {
						return entry?.sectionList && !!entry?.sectionList[templateName];
					} else {
						return undefined;
					}
				})
				.map((entry: any) => {
					return {
						t: entry?.sectionList[templateName].filter(
							(s: any) => s === templateSection,
						),
						m: entry?.component,
					};
				})
				.filter((entry: any) => entry?.t?.length)
				.map((entry: any) => entry?.m)
				.filter(Boolean);
		};

		// BasicTemplate -> mainSection
		let modulesInSectionLists = "";
		if (kind === "template") {
			modulesInSectionLists = getModulesFromTemplateSectionList(
				options.moduleSchemas,
				options.schema.component,
				key,
			).join("|");
		}

		// 1 - Mix whitelist and sectionList
		// 2 - Remove duplicate modules due to mixing between sectionList and whiteList
		const everyModuleInTemplateSection = [
			...new Set([...modulesInSectionLists.split("|"), ...whiteList]),
		].filter(Boolean);

		return `\n${doc}\n${key}${optional}: Fields.${returnTypeName}<${everyModuleInTemplateSection
			.map((component) => `${component}${interfaceSuffix}`)
			.join("|")}>;`;
	}

	// ComponentArray
	// <Complex>
	if (type === "ComponentArray") {
		// El type de Griddo es dinstinto para un ComponentArray si este está
		// dentro de un template.
		const returnTypeName =
			kind === "template" || kind === "pageContentType"
				? "TemplateSection"
				: "ComponentArray";

		const optional = kind === "template" ? "" : "?";

		// Type assertion, el field ComponentArray solo puede ser UI o Page
		const fieldCasted = field as
			| UI_Fields.ComponentArray
			| PageContentType_Fields.ComponentArray;

		const { key, _autoType, whiteList } = fieldCasted;
		const doc = _autoType?.doc || "";

		// TODO: Research to compute this with type itself.
		// SHAME: Remove anys!!
		const getModulesFromTemplateSectionList = (
			modules: Array<Module | undefined>,
			templateName: string,
			templateSection: string,
		): Array<Module | undefined> => {
			return modules
				.filter((entry: any) => {
					if (
						entry &&
						"schemaType" in entry &&
						entry?.schemaType === "module"
					) {
						return entry?.sectionList && !!entry?.sectionList[templateName];
					}

					return undefined;
				})
				.map((entry: any) => {
					return {
						t: entry?.sectionList[templateName].filter(
							(s: any) => s === templateSection,
						),
						m: entry?.component,
					};
				})
				.filter((entry: any) => entry?.t?.length)
				.map((entry: any) => entry?.m)
				.filter(Boolean);
		};

		// BasicTemplate -> mainSection
		let modulesInSectionLists = "";
		if (kind === "template") {
			modulesInSectionLists = getModulesFromTemplateSectionList(
				options.moduleSchemas,
				options.schema.component,
				key,
			).join("|");
		}

		// 1 - Mix whitelist and sectionList
		// 2 - Remove duplicate modules due to mixing between sectionList and whiteList
		const everyModuleInTemplateSection = [
			...new Set([...modulesInSectionLists.split("|"), ...whiteList]),
		].filter(Boolean);

		return `\n${doc}\n${key}${optional}: Fields.${returnTypeName}<${everyModuleInTemplateSection
			.map((component) => `${component}${interfaceSuffix}`)
			.join("|")}>;`;
	}

	// ComponentContainer
	// <Complex>
	if (type === "ComponentContainer") {
		// UI
		if (
			kind === "object" ||
			kind === "component" ||
			kind === "module" ||
			kind === "template" ||
			kind === "pageContentType"
		) {
			const FieldCasted = field as UI_Fields.ComponentContainer;
			const { whiteList } = FieldCasted;

			let generic = "";
			// getGenericFromWhiteListAndDefaultValues

			// -- El whitelist tiene más de un elemento, así que el type debe ser distinto
			// Un elemento = ComponentContainer<Card>
			// Más de uno  = ComponentContainer<{ card?: CardProps, image?: imageProps }>
			if (whiteList.length > 1 && "default" in options.schema) {
				// from ArrayFieldGroup
				if (options._from === "ArrayFieldGroup") {
					const fromKey = options._fromKey as string;

					if (options.schema.default[fromKey] === undefined) {
						console.warn(
							ERRORS.COMPONENT_CONTAINER_NO_DEFAULT_VALUE(
								field.type,
								field.key,
								options.schema.component,
							),
						);
					}

					const defVal = options.schema.default[fromKey] as Array<any>;
					generic =
						"{" +
						defVal
							.map((arrayFieldGroupFieldKey) => {
								if (!arrayFieldGroupFieldKey[field.key]) {
									console.warn(
										ERRORS.COMPONENT_CONTAINER_NO_DEFAULT_VALUE(
											field.type,
											field.key,
											options.schema.component,
										),
									);
									return "";
								} else {
									return Object.keys(arrayFieldGroupFieldKey[field.key]).map(
										(q) =>
											`${q}?:${arrayFieldGroupFieldKey[field.key][q].component}${interfaceSuffix}`,
									);
								}
							})
							.join("") +
						"}";
				} else {
					if (options.schema.default[field.key] === undefined) {
						console.warn(
							ERRORS.COMPONENT_CONTAINER_NO_DEFAULT_VALUE(
								field.type,
								field.key,
								options.schema.component,
							),
						);
						generic = "";
					} else {
						const defVal = options.schema.default[field.key];
						const genericStr = Object.keys(defVal)
							.map(
								(key) => `${key}?: ${defVal[key].component}${interfaceSuffix};`,
							)
							.join("");
						generic = `{${genericStr}}`;
					}
				}
			} else {
				generic = `${whiteList[0]}${interfaceSuffix}`;
			}

			return getGriddoFieldPropType(field, { generic });
		}
	}

	// FormContainer
	// <Complex>
	if (type === "FormContainer") {
		// UI
		if (
			kind === "object" ||
			kind === "component" ||
			kind === "module" ||
			kind === "template" ||
			kind === "pageContentType"
		) {
			const doc = _autoType?.doc || "";

			return `\n${doc}\n${key}?: Fields.FormContainer<FormTemplates${interfaceSuffix}>;`;
		}
	}

	// ReferenceField
	// TODO: Create correct generic using from/key values
	if (type === "ReferenceField") {
		const sources = field.sources || [{ structuredData: undefined }];
		const generic =
			(Array.isArray(sources) &&
				sources
					.map((item) =>
						item.structuredData
							? `${item.structuredData}${contentTypeSuffix}${interfaceSuffix}`
							: "unknown",
					)
					.join("|")) ||
			"";
		const referenceField = getGriddoFieldPropType(field, { generic });
		const queriedItems =
			hasDistributorData || getStaticData
				? `queriedItems?: Fields.QueriedData<${generic}>;`
				: "";

		return `
		 	${referenceField}
		 	${queriedItems}
		 `;

		// const { source } = field;
		// const generic = source
		// 	.map((item) => `${item}ContentType${interfaceSuffix}`)
		// 	.join("|");
		// const referenceField = getGriddoFieldPropType(field, { generic });
		// const queriedItems = hasDistributorData
		// 	? `queriedItems?: Fields.QueriedData<${generic}>;`
		// 	: "";

		// return `
		// 	${referenceField}
		// 	${queriedItems}
		// `;
	}

	// VisualUniqueSelection
	if (type === "VisualUniqueSelection") {
		const withValue = field.options[0] as ThumbnailOption;
		const withTheme = field.options[0] as ThemeVisualUniqueSelection;
		let generic = "";

		// ThumbnailOption
		if (withValue.value) {
			const all = field.options as Array<ThumbnailOption>;
			generic = all.map((item) => `"${item.value}"`).join("|");
		}

		// ThemeVisualUniqueSelection
		if (withTheme.theme) {
			const all = field.options as Array<ThemeVisualUniqueSelection>;
			generic = all[0].options.map((i) => `"${i.value}"`).join("|");
		}

		return getGriddoFieldPropType(field, { generic });
	}

	if (type === "FormCategorySelect") {
		return `${key}?: Fields.FormCategorySelect<FormCategoryLabels>`;
	}

	if (type === "AIReferenceField") {
		return `${key}?: Fields.AIReference`;
	}

	console.warn(ERRORS.FIELD_NOT_IMPLEMENTED(field.type));

	return "";
}

export {
	extractMergedSchemas,
	getGenericFromCheckBoxOptions,
	getGenericFromOptions,
	getGenericFromWhitelist,
	getGriddoFieldPropType,
	getTypeFromField,
	prettify,
	readConfig,
	resolveInstancePath,
	returnType,
};
